// /notary/documents/$group — Wave 62: หน้าเชิงลึกรายกลุ่มเอกสาร (TH)
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import type { NotaryDocGroup } from "@/data/notary-doc-groups";
import { APOSTILLE_STATUS } from "@/data/apostille-status";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/notary/documents/$group")({
  loader: async ({ params }) => {
    const { NOTARY_DOC_GROUPS, getNotaryDocGroup } = await import("@/data/notary-doc-groups");
    const group = getNotaryDocGroup(params.group);
    if (!group) throw notFound();
    return {
      __d: { NOTARY_DOC_GROUPS, getNotaryDocGroup }, group };
  },
  head: ({ params, loaderData }) => {
    const g = loaderData?.group;
    const path = `/notary/documents/${params.group}`;
    const url = `${SITE}${path}`;
    const enUrl = `${SITE}/en${path}`;
    if (!g) return {};
    const title = `${g.titleTh} — รับรองโดยทนายความ Notarial Services Attorney | NYC`;
    const desc = g.summaryTh.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, path, "th") },
        { name: "description", content: ctaDesc(desc, path, "th") },
        { property: "og:title", content: ctaTitle(title, path, "th") },
        { property: "og:description", content: ctaDesc(desc, path, "th") },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "@id": `${url}#breadcrumb`,
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Notary Public", item: `${SITE}/notary` },
              { "@type": "ListItem", position: 2, name: "เอกสารที่รับรองได้", item: `${SITE}/notary/documents` },
              { "@type": "ListItem", position: 3, name: g.titleTh, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "@id": `${url}#faq`,
            inLanguage: "th",
            mainEntity: g.faqsTh.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: NotaryDocGroupPage,
});

function NotaryDocGroupPage() {
  const { __d: { NOTARY_DOC_GROUPS, getNotaryDocGroup } } = Route.useLoaderData() as { __d: typeof import("@/data/notary-doc-groups") };
  const { group: g } = Route.useLoaderData() as { group: NotaryDocGroup };
  const others = NOTARY_DOC_GROUPS.filter((x) => x.slug !== g.slug);

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/notary" className="hover:underline">
          Notary Public
        </Link>{" "}
        /{" "}
        <Link to="/notary/documents" className="hover:underline">
          เอกสารที่รับรองได้
        </Link>{" "}
        / {g.titleTh}
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{g.titleTh}</h1>
      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        {g.summaryTh}
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">
        <strong>สถานะ Apostille ของไทย:</strong> ประเทศไทยยื่นภาคยานุวัติสารเมื่อ{" "}
        {APOSTILLE_STATUS.accessionDepositTh} และอนุสัญญาจะมีผลบังคับใช้ในวันที่{" "}
        {APOSTILLE_STATUS.entryIntoForceTh} ก่อนถึงวันดังกล่าวเอกสารยังต้องผ่านกรมการกงสุลและสถานทูตปลายทางตามเดิม{" "}
        <a
          href={APOSTILLE_STATUS.sourceUrl}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="underline"
        >
          ที่มา: HCCH
        </a>
      </aside>

      <div className="mt-8 space-y-4 text-base leading-8">
        {g.bodyTh.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <h2 className="mt-10 text-2xl font-semibold tracking-tight">เอกสารในกลุ่มนี้</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.documentsTh.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">ประเภทการรับรองที่ใช้</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.actsTh.map((a) => (
          <li key={a}>{a}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">เช็กลิสต์ก่อนเข้าพบทนาย</h2>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-7">
        {g.checklistTh.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>

      <h2 className="mt-8 text-2xl font-semibold tracking-tight">คำถามที่พบบ่อย</h2>
      <div className="mt-2 space-y-3">
        {g.faqsTh.map((f) => (
          <div key={f.q} className="rounded-lg border border-border p-4">
            <p className="font-medium">{f.q}</p>
            <p className="mt-1 text-sm leading-7 text-muted-foreground">{f.a}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-10 text-xl font-semibold tracking-tight">กลุ่มเอกสารอื่น</h2>
      <ul className="mt-2 grid gap-2 sm:grid-cols-2">
        {others.map((o) => (
          <li key={o.slug}>
            <Link
              to="/notary/documents/$group"
              params={{ group: o.slug }}
              className="text-primary hover:underline"
            >
              {o.titleTh}
            </Link>
          </li>
        ))}
      </ul>
          <ServiceAuthorityBlock topic="notary" locale="th" />
    </main>
  );
}
